import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, ArrowLeft, Fuel, Wrench, Shield, Home, RotateCcw, Info } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const CATEGORIES = [
  { slug: 'ultraleggeri', name: 'Ultraleggeri', fuelPerHr: 18, maintPerHr: 15, insuranceYr: 800, hangarMo: 150, annualInsp: 1500 },
  { slug: 'propulsione-singola', name: 'Propulsione Singola', fuelPerHr: 40, maintPerHr: 35, insuranceYr: 2500, hangarMo: 350, annualInsp: 3000 },
  { slug: 'propulsione-multipla', name: 'Propulsione Multipla', fuelPerHr: 90, maintPerHr: 80, insuranceYr: 5000, hangarMo: 500, annualInsp: 6000 },
  { slug: 'turboelica', name: 'Turboelica', fuelPerHr: 180, maintPerHr: 200, insuranceYr: 12000, hangarMo: 800, annualInsp: 15000 },
  { slug: 'jet', name: 'Jet', fuelPerHr: 500, maintPerHr: 600, insuranceYr: 30000, hangarMo: 2000, annualInsp: 40000 },
  { slug: 'elicotteri', name: 'Elicotteri', fuelPerHr: 120, maintPerHr: 250, insuranceYr: 10000, hangarMo: 600, annualInsp: 12000 },
];

function calculateCosts(form) {
  const cat = CATEGORIES.find(c => c.slug === form.category);
  if (!cat) return null;

  const hoursPerYear = parseInt(form.hoursPerYear) || 100;
  const fuelPrice = parseFloat(form.fuelPrice) || 2.2;

  const fuel = cat.fuelPerHr * fuelPrice * hoursPerYear;
  const maintenance = cat.maintPerHr * hoursPerYear;
  const insurance = cat.insuranceYr;
  const hangar = cat.hangarMo * 12;
  const annual = cat.annualInsp;
  const reserve = (fuel + maintenance) * 0.1; // 10% reserve

  const total = fuel + maintenance + insurance + hangar + annual + reserve;
  const perHour = total / hoursPerYear;

  return { fuel, maintenance, insurance, hangar, annual, reserve, total, perHour, hoursPerYear };
}

function formatEur(n) {
  return `€${Math.round(n).toLocaleString('it-IT')}`;
}

export default function CostCalculator() {
  const [form, setForm] = useState({ category: '', hoursPerYear: '100', fuelPrice: '2.20' });
  const [result, setResult] = useState(null);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const calculate = () => {
    setResult(calculateCosts(form));
  };

  const costItems = result ? [
    { icon: Fuel, label: 'Carburante', value: result.fuel, color: 'text-amber-400' },
    { icon: Wrench, label: 'Manutenzione', value: result.maintenance, color: 'text-blue-400' },
    { icon: Shield, label: 'Assicurazione', value: result.insurance, color: 'text-emerald-400' },
    { icon: Home, label: 'Hangaraggio', value: result.hangar, color: 'text-purple-400' },
    { icon: RotateCcw, label: 'Revisione Annuale', value: result.annual, color: 'text-rose-400' },
    { icon: Info, label: 'Riserva (10%)', value: result.reserve, color: 'text-zinc-400' },
  ] : [];

  return (
    <div className="min-h-screen bg-[#09090B] pt-20" data-testid="cost-calculator-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/services" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-[#C5A059] transition-colors mb-8">
          <ArrowLeft size={12} /> Tutti i servizi
        </Link>

        <div className="flex items-center gap-4 mb-2">
          <div className="w-14 h-14 bg-[#C5A059]/15 flex items-center justify-center">
            <Calculator size={26} className="text-[#C5A059]" />
          </div>
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-1">Strumento Gratuito</p>
            <h1 className="text-3xl md:text-4xl tracking-tight font-medium text-white flex items-center gap-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Operating Cost Calculator
              <span className="text-xs font-mono uppercase tracking-widest bg-[#C5A059]/20 text-[#C5A059] px-2.5 py-1 rounded-sm">Beta</span>
            </h1>
          </div>
        </div>

        {/* Beta Notice */}
        <div className="mt-4 mb-2 bg-amber-500/10 border border-amber-500/20 px-4 py-3" data-testid="beta-notice">
          <p className="text-xs text-amber-300/80 leading-relaxed">
            Questo strumento è in versione <strong>Beta</strong>. I costi calcolati sono indicativi e possono variare in base alla regione, al tipo di utilizzo e alle condizioni specifiche del velivolo. Per un'analisi dettagliata, contattaci tramite la sezione <a href="/services" className="text-[#C5A059] underline">Servizi</a>.
          </p>
        </div>

        <p className="text-sm text-zinc-400 mt-4 mb-10 max-w-2xl leading-relaxed">
          Scopri quanto costa realmente possedere e operare un velivolo. Il nostro calcolatore analizza tutte le voci di costo annuali: carburante, manutenzione, assicurazione, hangaraggio e revisioni.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Form */}
          <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 p-6 space-y-5">
            <div>
              <Label className="text-zinc-400 text-xs mb-1.5 block">Categoria Velivolo *</Label>
              <Select value={form.category} onValueChange={v => update('category', v)}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300 h-10 text-sm" data-testid="calc-category"><SelectValue placeholder="Seleziona categoria" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 z-[100]">
                  {CATEGORIES.map(c => <SelectItem key={c.slug} value={c.slug} className="text-zinc-300 text-sm">{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs mb-1.5 block">Ore di Volo Annue</Label>
              <Input type="number" value={form.hoursPerYear} onChange={e => update('hoursPerYear', e.target.value)} placeholder="100" className="bg-zinc-800 border-zinc-700 text-white h-10 text-sm font-mono" data-testid="calc-hours" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs mb-1.5 block">Prezzo Carburante (EUR/L)</Label>
              <Input type="number" step="0.01" value={form.fuelPrice} onChange={e => update('fuelPrice', e.target.value)} placeholder="2.20" className="bg-zinc-800 border-zinc-700 text-white h-10 text-sm font-mono" data-testid="calc-fuel-price" />
            </div>

            <button onClick={calculate} disabled={!form.category} className="w-full bg-[#C5A059] text-black py-3 text-sm font-medium hover:bg-[#D4B271] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid="calc-btn">
              Calcola Costi
            </button>
          </div>

          {/* Result */}
          <div className="lg:col-span-3 space-y-4">
            {result ? (
              <>
                {/* Total */}
                <div className="bg-zinc-900 border border-[#C5A059]/20 p-6" data-testid="calc-result">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059]">Costo Annuale Totale</p>
                    <span className="text-[10px] text-zinc-500">{result.hoursPerYear} ore/anno</span>
                  </div>
                  <div className="flex items-baseline gap-4 mb-2">
                    <p className="text-4xl font-bold text-white font-mono" data-testid="calc-total">{formatEur(result.total)}</p>
                    <p className="text-sm text-zinc-500">/anno</p>
                  </div>
                  <p className="text-sm text-[#C5A059] font-mono">{formatEur(result.perHour)} /ora di volo</p>
                </div>

                {/* Breakdown */}
                <div className="bg-zinc-900 border border-zinc-800 p-6" data-testid="calc-breakdown">
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-5">Dettaglio Costi</p>
                  <div className="space-y-4">
                    {costItems.map(item => {
                      const pct = Math.round((item.value / result.total) * 100);
                      return (
                        <div key={item.label}>
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-2">
                              <item.icon size={14} className={item.color} />
                              <span className="text-sm text-zinc-300">{item.label}</span>
                            </div>
                            <span className="text-sm text-white font-mono">{formatEur(item.value)}</span>
                          </div>
                          <div className="h-1.5 bg-zinc-800 overflow-hidden">
                            <div className={`h-full ${item.color.replace('text-', 'bg-')} opacity-60`} style={{ width: `${pct}%` }} />
                          </div>
                          <p className="text-[10px] text-zinc-600 mt-0.5 text-right">{pct}%</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="p-3 bg-zinc-800/50 border border-zinc-700/50">
                  <p className="text-[10px] text-zinc-500 leading-relaxed">I costi sono stime basate su medie di mercato europeo e possono variare significativamente in base al modello specifico, alla regione, al tipo di utilizzo e alla manutenzione pregressa.</p>
                </div>

                <Link to="/listings" className="flex items-center justify-center gap-2 border border-[#C5A059] text-[#C5A059] py-2.5 text-xs font-medium hover:bg-[#C5A059] hover:text-black transition-colors" data-testid="browse-listings-btn">
                  Cerca il tuo velivolo
                </Link>
              </>
            ) : (
              <div className="bg-zinc-900 border border-zinc-800 p-8 text-center">
                <Calculator size={36} className="mx-auto mb-4 text-zinc-700" />
                <p className="text-sm text-zinc-500 mb-2">Seleziona la categoria e le ore di volo annue</p>
                <p className="text-xs text-zinc-600">I risultati includeranno carburante, manutenzione, assicurazione, hangar e revisione annuale</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
